import PropTypes from 'prop-types';
import React from 'react';
import { WeezHeader, Button, Card, ContentCard, FilterSetController, Filter, EmptyState, Icon, Input, Modal, ToastContext, Message } from '@weezevent/nacre';
import TicketOffice from '../../models/ticket_office';
import { Loader } from '@weezevent/throne';
import { Trans, useTranslation } from 'react-i18next';
import TicketOfficesLine from './list/ticketOfficesLine';
import CreditsCard from './statistics/creditsCard';
import { useFilters, handleSearchQuery } from '../manager/components/context';
import { fetcher } from '@weezevent/weezjs-core';
import { useHistory } from 'react-router-dom';
import Style from './list/listTicketOffices.module.css';

const initialFilters = {
    deleted__in: new Set(['0'])
};

// Use Trans instead of t to declare and export const outside a component
const optionsStatus = [
    {
        key: 'deleted',
        text: <Trans i18nKey="ticket.ticket_offices.filters.deleted" />,
        name: '1'
    },
    {
        key: 'active',
        text: <Trans i18nKey="ticket.ticket_offices.filters.active" />,
        name: '0'
    }
];

const spec = {
    multiple: ['deleted__in'],
    fields: ['deleted__in', 'search'],
    aliases: {},
    date: []
};
const SetNewTicketOffices = ({ displayModal, setDisplayModal, organization, ticketsOffices, syncTicketOffices }) => {
    const toast = React.useContext(ToastContext);
    const { t } = useTranslation();
    const history = useHistory();
    const [inputValue, setInputValue] = React.useState(`Ticket Office #${ticketsOffices && ticketsOffices.length + 1}`);
    const [isLoading, setIsLoading] = React.useState(false);
    const data = React.useMemo(() => {
        return { organization_id: organization.id, name: inputValue };
    }, [inputValue]);

    const nameAlreadyExist = React.useMemo(() => {
        return ticketsOffices.some(ticketOffice => ticketOffice.name.trim().toLowerCase() === inputValue.trim().toLowerCase());
    }, [ticketsOffices, inputValue]);

    const createTicketOffice = e => {
        setIsLoading(true);
        fetcher
            .post(`/ticket/organizations/${organization.id}/ticket_office`, { data })
            .then(data => {
                syncTicketOffices({
                    allow_cache: false,
                    cache: false
                });
                toast.success(t('ticket.ticket_offices.create_operator.sucess'));
                history.push(`/ticket/O${organization.id}/ticket_offices/${data.response['id_guichet']}/dashboard`);
            })
            .catch(() => toast.error('ticket.ticket_offices.create_operator.error'));
    };

    return (
        <Modal
            size={'medium'}
            open={displayModal}
            onClose={() => setDisplayModal(false)}
            title={t('ticket.ticket_offices.create_operator.title')}
            submitButton={<Button primary onClick={createTicketOffice} label={t('ticket.common.action.save')} isLoading={isLoading} />}
            cancelButton={<Button onClick={() => setDisplayModal(false)} label={t('ticket.common.action.cancel')} />}
        >
            {t('ticket.ticket_offices.create_operator.subtitle')}
            <Input value={inputValue} onChange={(synth, { value }) => setInputValue(value)} />
            {nameAlreadyExist && (
                <div className={Style['creating-message-danger']}>
                    <Message type="danger">{t('ticket.ticket_offices.create_operator.message')}</Message>
                </div>
            )}
        </Modal>
    );
};

export function OpenCreationModal({ organization, ticketsOffices, syncTicketOffices }) {
    const { t } = useTranslation();
    const [displayModal, setDisplayModal] = React.useState(false);
    const createTicketOffice = React.useCallback(() => {
        setDisplayModal(true);
    }, []);

    return (
        <>
            <Button primary icon={<Icon name="plus" />} onClick={createTicketOffice} label={t('ticket.ticket_offices.template.cta')} />
            {displayModal && (
                <SetNewTicketOffices
                    organization={organization}
                    ticketsOffices={ticketsOffices}
                    syncTicketOffices={syncTicketOffices}
                    displayModal={displayModal}
                    setDisplayModal={setDisplayModal}
                />
            )}
        </>
    );
}

export default function TicketOfficesTemplate({ organization }) {
    const { t } = useTranslation();
    const [filters, setFilters] = useFilters(spec, initialFilters);
    const [, setAllSelected] = React.useState(false);
    const [ticketsOffices, loadingTicketsOffices, syncTicketOffices] = TicketOffice.useApiModel(
        {
            organizationId: organization.id,

            query: {
                ...handleSearchQuery(spec, filters, true)
            }
        },
        [filters, spec, organization]
    );

    const onChangeSearch = React.useCallback(
        value => {
            let tmpFilters = { ...filters };
            if (value !== '' && value !== null) {
                tmpFilters = { ...tmpFilters, search: value };
            } else {
                delete tmpFilters.search;
            }
            setFilters(tmpFilters);
        },
        [filters]
    );

    const resetSelected = React.useCallback(() => {
        setAllSelected(false);
    });

    return (
        <>
            <WeezHeader
                title={t('ticket.ticket_offices.template.pageTitle')}
                rightComponent={[
                    <a key={1} href={window.weezConfig.ticket_office} target="_blank" rel="noopener noreferrer" style={{ marginRight: '15px' }}>
                        <Button label={[<Icon name="globe" key={1} />, t('ticket.ticket_offices.template.ButtonLabel')]} />
                    </a>,
                    <OpenCreationModal key={1} organization={organization} ticketsOffices={ticketsOffices} syncTicketOffices={syncTicketOffices} />
                ]}
            >
                {t('ticket.ticket_offices.template.header_description')}
            </WeezHeader>

            <CreditsCard organization={organization} />
            <Card>
                <ContentCard>
                    <FilterSetController
                        total={ticketsOffices && ticketsOffices.length}
                        labelTotal={t('ticket.common.ticket_office', { count: ticketsOffices && ticketsOffices.length })}
                        labelReset={t('ticket.common.action.reset')}
                        labelFilters={t('ticket.common.cta.filters')}
                        search={{
                            onChange: (synth, value) => {
                                onChangeSearch(value);
                                resetSelected();
                            },
                            handleReset: (synth, value) => {
                                onChangeSearch(null);
                            },
                            displayResetIcon: true,
                            placeholder: t('ticket.common.action.search'),
                            value: filters?.search || ''
                        }}
                        onChange={values => {
                            if (filters.search !== undefined && filters.search.length > 0) {
                                values.search = filters.search;
                            } else {
                                delete values.search;
                            }
                            setFilters(values);
                            resetSelected();
                        }}
                        initialValues={() => {
                            return { ...filters, ...initialFilters };
                        }}
                        defaultValues={initialFilters}
                        onReset={() => {}}
                    >
                        <Filter
                            index="1"
                            labelSelectAll={t('ticket.common.action.select-all')}
                            labelCTA={t('ticket.common.action.apply')}
                            labelName={t('ticket.ticket_offices.filters.status')}
                            multiple
                            name="deleted__in"
                            options={optionsStatus}
                        />
                    </FilterSetController>
                </ContentCard>
            </Card>
            {loadingTicketsOffices && (
                <Card>
                    <Card.Content style={{ padding: '67.08px' }}>
                        <Loader active inline="centered" className="loader" />
                    </Card.Content>
                </Card>
            )}

            {!loadingTicketsOffices && ticketsOffices && (
                <>
                    {ticketsOffices.map((ticket_office, index) => (
                        <TicketOfficesLine key={index} ticket_office={ticket_office} organization={organization} syncTicketOffices={syncTicketOffices} />
                    ))}
                </>
            )}

            {!loadingTicketsOffices && !ticketsOffices && (
                <Card>
                    <Card.Content>
                        <EmptyState icon={<Icon name="tickets" />} title={t('noTicketOfficesYet')} />
                    </Card.Content>
                </Card>
            )}
        </>
    );
}

TicketOfficesTemplate.propTypes = {
    match: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired
};
